import * as React from 'react';

import { setLocation } from 'js/lib/coursera.redirect';
import { get as userGet } from 'js/lib/user';

import { Button, Typography2 } from '@coursera/cds-core';
import { StyleSheet, css } from '@coursera/coursera-ui';

import CourseAndPartnerName from 'bundles/course-cards/components/course-card/enterprise/CourseAndPartnerName';
import CourseS12nPhoto from 'bundles/course-cards/components/course-card/enterprise/CourseS12nPhoto';
import TogglableContent from 'bundles/course-cards/components/course-card/enterprise/TogglableContent';
import { useNaptime } from 'bundles/naptimejs';
import type CoursesV1 from 'bundles/naptimejs/resources/courses.v1';
// @ts-expect-error TS7016 Untyped import http://go.dkandu.me/strict-ts-migration#TS7016
import ProgramEnrollmentsV2 from 'bundles/naptimejs/resources/programEnrollments.v2';
import type { EnterpriseBadge } from 'bundles/page-config-common/providers/enterprise/EnterpriseBadgeCollectionsProvider';
import { useProgram } from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { getCourseProductUrl } from 'bundles/unified-description-page-common/utils';

import _t from 'i18n!nls/course-cards';

const styles = StyleSheet.create({
  SkillPathwayCourseNotEnrolledCard: {
    minHeight: 104,
  },
});

type Props = {
  course: CoursesV1;
  isPartOfS12n?: boolean;
  programId: string;
  badgeParent: EnterpriseBadge;
};

const SkillPathwayCourseNotEnrolledCard = ({
  course: { id, name, photoUrl, slug, description },
  badgeParent,
  isPartOfS12n,
  programId,
}: Props): JSX.Element => {
  const [isEnrolling, setIsEnrolling] = React.useState(false);
  const [enrollmentError, setEnrollmentError] = React.useState(false);
  const program = useProgram();
  const { naptime } = useNaptime(() => ({}));
  const onActionSuccess = () => {
    const baseHomeUrl = getCourseProductUrl(slug, false);
    setLocation(`${baseHomeUrl}/home`);
  };

  const onEnrollClick = async () => {
    try {
      setIsEnrolling(true);

      await naptime.executeMutation(
        ProgramEnrollmentsV2.action(
          'enrollInCourse',
          {},
          {
            programId,
            courseId: id,
            userId: userGet().id.toString(),
            collectionId: badgeParent.collectionId,
          }
        )
      );

      onActionSuccess();
    } catch (error) {
      setIsEnrolling(false);
      setEnrollmentError(true);
      console.error(error);
    }
  };

  return (
    <div
      {...css('rc-SkillPathwayCourseNotEnrolledCard horizontal-box wrap', styles.SkillPathwayCourseNotEnrolledCard)}
      data-e2e={`SkillPathwayCourseNotEnrolledCard~${id}`}
    >
      <CourseS12nPhoto
        imgixClassName="course-logo"
        imageSrc={photoUrl}
        slug={slug}
        id={id}
        title={name}
        onClick={() =>
          program?.metadata?.slug &&
          setLocation(`/programs/${program?.metadata?.slug}/learning-path/${badgeParent.slug}`)
        }
      />
      <div className="flex-1 vertical-box">
        <CourseAndPartnerName
          courseId={id}
          courseName={name}
          isPartOfS12n={isPartOfS12n}
          onClick={() =>
            program?.metadata?.slug &&
            setLocation(`/programs/${program?.metadata?.slug}/learning-path/${badgeParent.slug}`)
          }
        />
        {isPartOfS12n && (
          <Typography2 component="p" className="m-b-1" variant="bodyPrimary">
            {_t('Not Started')}
          </Typography2>
        )}
        {description && (
          <TogglableContent moreLessButtonContextLabel={_t('About #{courseName}', { courseName: name })}>
            <Typography2 component="p" variant="bodySecondary">
              {description}
            </Typography2>
          </TogglableContent>
        )}
      </div>
      <Button
        variant="primary"
        size="small"
        onClick={onEnrollClick}
        loading={isEnrolling}
        disabled={enrollmentError}
        style={{ alignSelf: 'center', height: '36px', marginLeft: '15px' }}
      >
        {_t('Start learning')}
      </Button>
    </div>
  );
};

export default SkillPathwayCourseNotEnrolledCard;
